import { supabase } from './supabase';

export const uploadVenuePicture = async (
  venueId: string,
  file: File,
  category: PictureCategory
): Promise<string> => {
  try {
    console.log(`Starting upload for venue ${venueId}, category ${category}, file: ${file.name}`);
    
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${venueId}/${category}/${fileName}`;

    console.log(`Generated file path: ${filePath}`);

    // Determine content type based on file extension
    let contentType = file.type;
    if (fileExt === 'jpg' || fileExt === 'jpeg') {
      contentType = 'image/jpeg';
    } else if (fileExt === 'png') {
      contentType = 'image/png';
    } else if (fileExt === 'gif') {
      contentType = 'image/gif';
    }

    // Implement retry mechanism for uploads
    let retries = 0;
    const maxRetries = 3;
    let uploadError = null;
    
    while (retries < maxRetries) {
      try {
        console.log(`Upload attempt ${retries + 1} for ${filePath}`);
        const { error } = await supabase.storage
          .from('venue-pictures')
          .upload(filePath, file, {
            upsert: true, // Allow overwriting existing files
            contentType: contentType
          });
          
        if (!error) {
          console.log(`Upload successful after ${retries} retries`);
          uploadError = null;
          break;
        }
        
        uploadError = error;
        console.warn(`Upload attempt ${retries + 1} failed:`, error);
        retries++;
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
      } catch (err) {
        uploadError = err;
        console.warn(`Upload attempt ${retries + 1} failed with exception:`, err);
        retries++;
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
      }
    }

    if (uploadError) {
      console.error('Upload failed after all retry attempts:', uploadError);
      throw uploadError;
    }

    console.log('Upload successful, getting public URL');

    // Get public URL with retries
    let publicUrl = '';
    retries = 0;
    
    while (retries < maxRetries && !publicUrl) {
      try {
        const { data: urlData } = supabase.storage
          .from('venue-pictures')
          .getPublicUrl(filePath);

        if (!urlData?.publicUrl) {
          console.warn(`Empty public URL returned (attempt ${retries + 1})`);
          retries++;
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
          continue;
        }

        publicUrl = urlData.publicUrl;
        break;
      } catch (err) {
        console.warn(`Exception getting public URL (attempt ${retries + 1}):`, err);
        retries++;
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
      }
    }

    if (!publicUrl) {
      throw new Error('Failed to get valid public URL after multiple attempts');
    }

    // Check if picture already exists in database
    const { data: existingPicture, error: checkError } = await supabase
      .from('restaurant_pictures')
      .select('id')
      .eq('restaurant_id', venueId)
      .eq('url', publicUrl)
      .maybeSingle();

    if (checkError) {
      console.error('Error checking for existing picture:', checkError);
    } else if (existingPicture) {
      console.log('Picture already exists in database, skipping insertion');
      return publicUrl;
    }

    // Add the picture to the restaurant_pictures table with retries
    retries = 0;
    let insertError = null;

    while (retries < maxRetries) {
      try {
        const { error } = await supabase
          .from('restaurant_pictures')
          .insert({
            restaurant_id: venueId,
            category: category,
            url: publicUrl,
            file_name: fileName,
            file_size: file.size,
            content_type: contentType,
            storage_path: filePath
          });

        if (!error) {
          console.log('Picture added to restaurant_pictures table successfully');
          insertError = null;
          break;
        }

        insertError = error;
        console.warn(`Insert attempt ${retries + 1} failed:`, error);
        
        // If it's a duplicate key error, the picture is already in the database
        if (error.code === '23505') {
          console.log('Picture already exists in database (duplicate key)');
          return publicUrl;
        }

        retries++;
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
      } catch (err) {
        insertError = err;
        console.warn(`Insert attempt ${retries + 1} failed with exception:`, err);
        retries++;
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
      }
    }

    if (insertError) {
      console.error('Failed to add picture to database after all retry attempts:', insertError);
      // Don't throw the error, as we still want to return the URL
    }

    return publicUrl;
  } catch (err) {
    console.error('Error uploading venue picture:', err);
    throw err;
  }
};

export const updateVenuePictures = async (
  venueId: string,
  venueType: 'restaurant' | 'bar',
  pictures: {
    gallery: string[];
    menu: string[];
    interior: string[];
    exterior: string[];
  }
) => {
  try {
    console.log(`Updating pictures for ${venueType} ${venueId}:`, pictures);
    
    if (venueType === 'restaurant') {
      // For restaurants, use the restaurant_pictures table
      for (const category in pictures) {
        const typedCategory = category as keyof typeof pictures;
        for (const url of pictures[typedCategory]) {
          if (!url) continue;
          
          try {
            // Check if picture already exists
            const { data: existingPicture, error: checkError } = await supabase
              .from('restaurant_pictures')
              .select('id')
              .eq('restaurant_id', venueId)
              .eq('url', url)
              .maybeSingle();

            if (checkError) {
              console.error('Error checking for existing picture:', checkError);
              continue;
            }

            if (existingPicture) {
              console.log(`Picture already exists: ${url}`);
              continue;
            }

            // Add new picture
            const { error: insertError } = await supabase
              .from('restaurant_pictures')
              .insert({
                restaurant_id: venueId,
                category: typedCategory,
                url: url
              });

            if (insertError) {
              console.error(`Error adding picture to restaurant_pictures table (${typedCategory}):`, insertError);
            }
          } catch (err) {
            console.error(`Exception adding picture to restaurant_pictures table (${typedCategory}):`, err);
          }
        }
      }
    } else {
      // For bars, use the old method
      // First get the current pictures
      const { data: venue, error: getError } = await supabase
        .from('bars')
        .select('pictures')
        .eq('id', venueId)
        .single();
      
      if (getError) {
        console.error('Error getting current pictures:', getError);
        throw getError;
      }
      
      // Initialize default structure if pictures is null
      const currentPictures = venue?.pictures || {
        gallery: [],
        menu: [],
        interior: [],
        exterior: []
      };
      
      console.log('Current pictures:', currentPictures);
      
      // Ensure all arrays exist in the current pictures
      const normalizedCurrentPictures = {
        gallery: Array.isArray(currentPictures.gallery) ? currentPictures.gallery : [],
        menu: Array.isArray(currentPictures.menu) ? currentPictures.menu : [],
        interior: Array.isArray(currentPictures.interior) ? currentPictures.interior : [],
        exterior: Array.isArray(currentPictures.exterior) ? currentPictures.exterior : []
      };

      // Verify all new URLs before merging
      const verifiedUrls = {
        gallery: [],
        menu: [],
        interior: [],
        exterior: []
      };

      // Add a delay between URL verifications to avoid rate limiting
      const verifyWithDelay = async (url: string) => {
        await new Promise(resolve => setTimeout(resolve, 500));
        return await isValidImageUrl(url);
      };

      for (const category in pictures) {
        const typedCategory = category as keyof typeof pictures;
        for (const url of pictures[typedCategory]) {
          if (await verifyWithDelay(url)) {
            verifiedUrls[typedCategory].push(url);
          } else {
            console.warn(`Invalid URL detected and skipped: ${url}`);
            
            // Try to verify if the file exists in storage
            try {
              const urlParts = new URL(url);
              const pathParts = urlParts.pathname.split('/');
              const filePath = pathParts.slice(pathParts.indexOf('venue-pictures') + 1).join('/');
              
              const { data: checkData } = await supabase.storage
                .from('venue-pictures')
                .list(venueId + '/' + typedCategory);
                
              const fileName = filePath.split('/').pop();
              if (checkData?.some(f => f.name === fileName)) {
                console.log(`File exists in storage but URL validation failed. Adding URL: ${url}`);
                verifiedUrls[typedCategory].push(url);
              }
            } catch (err) {
              console.error('Error checking file in storage:', err);
            }
          }
        }
      }
      
      // Merge existing pictures with verified new ones
      const updatedPictures = {
        gallery: [...normalizedCurrentPictures.gallery, ...verifiedUrls.gallery],
        menu: [...normalizedCurrentPictures.menu, ...verifiedUrls.menu],
        interior: [...normalizedCurrentPictures.interior, ...verifiedUrls.interior],
        exterior: [...normalizedCurrentPictures.exterior, ...verifiedUrls.exterior]
      };
      
      console.log('Merged pictures:', updatedPictures);
      
      // Implement retry mechanism for database updates
      let retries = 0;
      const maxRetries = 3;
      let updateError = null;

      while (retries < maxRetries) {
        try {
          const { error } = await supabase
            .from('bars')
            .update({ pictures: updatedPictures })
            .eq('id', venueId);

          if (!error) {
            console.log('Pictures updated successfully');
            updateError = null;
            break;
          }

          updateError = error;
          console.warn(`Update attempt ${retries + 1} failed:`, error);
          retries++;
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
        } catch (err) {
          updateError = err;
          console.warn(`Update attempt ${retries + 1} failed with exception:`, err);
          retries++;
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
        }
      }

      if (updateError) {
        throw updateError;
      }

      // Verify the update was successful
      const { data: verifiedVenue, error: verifyError } = await supabase
        .from('bars')
        .select('pictures')
        .eq('id', venueId)
        .single();
      
      if (verifyError) {
        console.error('Error verifying pictures update:', verifyError);
        throw verifyError;
      }

      console.log('Verified venue pictures:', verifiedVenue.pictures);
    }
  } catch (err) {
    console.error('Error updating venue pictures:', err);
    throw err;
  }
};

export const getVenuePictures = async (
  venueId: string,
  venueType: 'restaurant' | 'bar'
): Promise<{
  gallery: string[];
  menu: string[];
  interior: string[];
  exterior: string[];
}> => {
  try {
    if (venueType === 'restaurant') {
      // For restaurants, use the new restaurant_pictures table via the function
      const { data, error } = await supabase.rpc('get_restaurant_pictures', {
        p_restaurant_id: venueId
      });
      
      if (error) throw error;
      
      return {
        gallery: data?.gallery || [],
        menu: data?.menu || [],
        interior: data?.interior || [],
        exterior: data?.exterior || []
      };
    } else {
      // For bars, use the old method
      const { data, error } = await supabase
        .from('bars')
        .select('pictures')
        .eq('id', venueId)
        .single();

      if (error) throw error;

      // Ensure we return a properly structured object even if data.pictures is null
      if (!data || !data.pictures) {
        return {
          gallery: [],
          menu: [],
          interior: [],
          exterior: []
        };
      }

      // Normalize the pictures object to ensure all arrays exist
      return {
        gallery: Array.isArray(data.pictures.gallery) ? data.pictures.gallery : [],
        menu: Array.isArray(data.pictures.menu) ? data.pictures.menu : [],
        interior: Array.isArray(data.pictures.interior) ? data.pictures.interior : [],
        exterior: Array.isArray(data.pictures.exterior) ? data.pictures.exterior : []
      };
    }
  } catch (err) {
    console.error('Error getting venue pictures:', err);
    // Return empty arrays instead of throwing to prevent UI errors
    return {
      gallery: [],
      menu: [],
      interior: [],
      exterior: []
    };
  }
};

// Function to verify and fix restaurant pictures
export const verifyAndFixRestaurantPictures = async () => {
  try {
    console.log('Starting verification of restaurant pictures...');
    
    // Get all restaurants
    const { data: restaurants, error: fetchError } = await supabase
      .from('restaurants')
      .select('id, pictures');
    
    if (fetchError) {
      console.error('Error fetching restaurants:', fetchError);
      throw fetchError;
    }
    
    console.log(`Found ${restaurants?.length || 0} restaurants to check`);
    
    // Check each restaurant
    for (const restaurant of restaurants || []) {
      console.log(`Checking restaurant ${restaurant.id}...`);
      
      // Check if pictures is null or missing required structure
      if (!restaurant.pictures || 
          !restaurant.pictures.gallery || 
          !restaurant.pictures.menu || 
          !restaurant.pictures.interior || 
          !restaurant.pictures.exterior) {
        
        console.log(`Restaurant ${restaurant.id} has invalid pictures structure, fixing...`);
        
        // Initialize default structure
        const fixedPictures = {
          gallery: Array.isArray(restaurant.pictures?.gallery) ? restaurant.pictures.gallery : [],
          menu: Array.isArray(restaurant.pictures?.menu) ? restaurant.pictures.menu : [],
          interior: Array.isArray(restaurant.pictures?.interior) ? restaurant.pictures.interior : [],
          exterior: Array.isArray(restaurant.pictures?.exterior) ? restaurant.pictures.exterior : []
        };
        
        // Update the restaurant with fixed pictures structure
        const { error: updateError } = await supabase
          .from('restaurants')
          .update({ pictures: fixedPictures })
          .eq('id', restaurant.id);
        
        if (updateError) {
          console.error(`Error updating restaurant ${restaurant.id}:`, updateError);
        } else {
          console.log(`Fixed pictures structure for restaurant ${restaurant.id}`);
        }
      } else {
        console.log(`Restaurant ${restaurant.id} has valid pictures structure`);
      }
      
      // Verify storage bucket for this restaurant
      await verifyRestaurantStorageBucket(restaurant.id);
    }
    
    console.log('Restaurant pictures verification completed');
    return { success: true, message: 'Verification completed' };
  } catch (err) {
    console.error('Error verifying restaurant pictures:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
};

// Function to verify storage bucket for a restaurant
const verifyRestaurantStorageBucket = async (restaurantId: string) => {
  try {
    console.log(`Checking storage bucket for restaurant ${restaurantId}...`);
    
    // List all files in the restaurant's folder
    const { data: files, error: listError } = await supabase.storage
      .from('venue-pictures')
      .list(restaurantId);
    
    if (listError) {
      console.error(`Error listing files for restaurant ${restaurantId}:`, listError);
      return;
    }
    
    console.log(`Found ${files?.length || 0} files/folders for restaurant ${restaurantId}`);
    
    // Check each category folder
    for (const category of ['gallery', 'menu', 'interior', 'exterior']) {
      // Remove the check for categoryFolder.id since it's not relevant for folders
      if (files?.find(f => f.name === category)) {
        console.log(`Found ${category} folder for restaurant ${restaurantId}`);
        
        // List files in the category folder
        const { data: categoryFiles, error: categoryError } = await supabase.storage
          .from('venue-pictures')
          .list(`${restaurantId}/${category}`);
        
        if (categoryError) {
          console.error(`Error listing ${category} files for restaurant ${restaurantId}:`, categoryError);
          continue;
        }
        
        console.log(`Found ${categoryFiles?.length || 0} files in ${category} folder`);
        
        // Get the restaurant's current pictures
        const { data: restaurant, error: getError } = await supabase
          .from('restaurants')
          .select('pictures')
          .eq('id', restaurantId)
          .single();
        
        if (getError) {
          console.error(`Error getting restaurant ${restaurantId}:`, getError);
          continue;
        }
        
        // Check if all files in storage are in the pictures column
        const storedUrls = categoryFiles?.map(file => {
          const publicUrl = supabase.storage
            .from('venue-pictures')
            .getPublicUrl(`${restaurantId}/${category}/${file.name}`).data.publicUrl;
          return publicUrl;
        }) || [];
        
        const currentUrls = Array.isArray(restaurant.pictures?.[category]) ? 
          restaurant.pictures[category] : [];
        
        console.log(`${category} - Storage files: ${storedUrls.length}, Database URLs: ${currentUrls.length}`);
        
        // Find missing URLs (in storage but not in database)
        const missingUrls = storedUrls.filter(url => !currentUrls.includes(url));
        
        if (missingUrls.length > 0) {
          console.log(`Found ${missingUrls.length} missing ${category} URLs for restaurant ${restaurantId}, adding to database...`);
          
          // Add each missing URL to the restaurant_pictures table
          for (const url of missingUrls) {
            try {
              // Call the database function to add the picture
              const { error } = await supabase.rpc('add_restaurant_picture', {
                p_restaurant_id: restaurantId,
                p_category: category,
                p_url: url
              });
              
              if (error) {
                console.error(`Error adding picture to restaurant_pictures table (${category}):`, error);
              }
            } catch (err) {
              console.error(`Exception adding picture to restaurant_pictures table (${category}):`, err);
            }
          }
        } else {
          console.log(`No missing ${category} URLs for restaurant ${restaurantId}`);
        }
      } else {
        console.log(`No ${category} folder found for restaurant ${restaurantId}`);
      }
    }
  } catch (err) {
    console.error(`Error verifying storage bucket for restaurant ${restaurantId}:`, err);
  }
};

// Function to fix a specific restaurant's pictures
export const fixRestaurantPictures = async (restaurantId: string) => {
  try {
    console.log(`Fixing pictures for restaurant ${restaurantId}...`);
    
    // Get the restaurant
    const { data: restaurant, error: getError } = await supabase
      .from('restaurants')
      .select('pictures')
      .eq('id', restaurantId)
      .single();
    
    if (getError) {
      console.error(`Error getting restaurant ${restaurantId}:`, getError);
      throw getError;
    }
    
    // Initialize default structure
    const fixedPictures = {
      gallery: Array.isArray(restaurant.pictures?.gallery) ? restaurant.pictures.gallery : [],
      menu: Array.isArray(restaurant.pictures?.menu) ? restaurant.pictures.menu : [],
      interior: Array.isArray(restaurant.pictures?.interior) ? restaurant.pictures.interior : [],
      exterior: Array.isArray(restaurant.pictures?.exterior) ? restaurant.pictures.exterior : []
    };
    
    // Update the restaurant with fixed pictures structure
    const { error: updateError } = await supabase
      .from('restaurants')
      .update({ pictures: fixedPictures })
      .eq('id', restaurantId);
    
    if (updateError) {
      console.error(`Error updating restaurant ${restaurantId}:`, updateError);
      throw updateError;
    }
    
    console.log(`Fixed pictures structure for restaurant ${restaurantId}`);
    
    // Verify storage bucket for this restaurant
    await verifyRestaurantStorageBucket(restaurantId);
    
    // Migrate pictures to the new restaurant_pictures table
    for (const category in fixedPictures) {
      const typedCategory = category as keyof typeof fixedPictures;
      for (const url of fixedPictures[typedCategory]) {
        try {
          // Call the database function to add the picture
          const { error } = await supabase.rpc('add_restaurant_picture', {
            p_restaurant_id: restaurantId,
            p_category: typedCategory,
            p_url: url
          });
          
          if (error) {
            console.error(`Error adding picture to restaurant_pictures table (${typedCategory}):`, error);
          }
        } catch (err) {
          console.error(`Exception adding picture to restaurant_pictures table (${typedCategory}):`, err);
        }
      }
    }
    
    return { success: true, message: 'Restaurant pictures fixed' };
  } catch (err) {
    console.error(`Error fixing restaurant ${restaurantId} pictures:`, err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
};

// Function to sync all restaurant pictures with storage
export const syncAllRestaurantPictures = async () => {
  try {
    console.log('Starting synchronization of all restaurant pictures with storage...');
    
    // Get all restaurants
    const { data: restaurants, error: fetchError } = await supabase
      .from('restaurants')
      .select('id, name');
    
    if (fetchError) {
      console.error('Error fetching restaurants:', fetchError);
      throw fetchError;
    }
    
    console.log(`Found ${restaurants?.length || 0} restaurants to sync`);
    
    // Process each restaurant
    for (const restaurant of restaurants || []) {
      console.log(`Syncing pictures for restaurant ${restaurant.name} (${restaurant.id})...`);
      await syncRestaurantPictures(restaurant.id);
    }
    
    console.log('Restaurant pictures synchronization completed');
    return { success: true, message: 'All restaurants synced successfully' };
  } catch (err) {
    console.error('Error syncing restaurant pictures:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
};

// Function to sync a specific restaurant's pictures with storage
export const syncRestaurantPictures = async (restaurantId: string) => {
  try {
    console.log(`Syncing pictures for restaurant ${restaurantId}...`);
    
    // Get all files from storage for this restaurant
    const storageUrls = await getAllRestaurantStorageUrls(restaurantId);
    
    // Add each URL to the restaurant_pictures table
    for (const category in storageUrls) {
      const typedCategory = category as keyof typeof storageUrls;
      for (const url of storageUrls[typedCategory]) {
        try {
          // Call the database function to add the picture
          const { error } = await supabase.rpc('add_restaurant_picture', {
            p_restaurant_id: restaurantId,
            p_category: typedCategory,
            p_url: url
          });
          
          if (error) {
            console.error(`Error adding picture to restaurant_pictures table (${typedCategory}):`, error);
          }
        } catch (err) {
          console.error(`Exception adding picture to restaurant_pictures table (${typedCategory}):`, err);
        }
      }
    }
    
    console.log(`Successfully synced pictures for restaurant ${restaurantId}`);
    return { success: true, message: 'Restaurant pictures synced successfully' };
  } catch (err) {
    console.error(`Error syncing restaurant ${restaurantId} pictures:`, err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
};

// Function to get all storage URLs for a restaurant
const getAllRestaurantStorageUrls = async (restaurantId: string) => {
  const result = {
    gallery: [] as string[],
    menu: [] as string[],
    interior: [] as string[],
    exterior: [] as string[]
  };
  
  try {
    // List all folders in the restaurant's directory
    const { data: folders, error: foldersError } = await supabase.storage
      .from('venue-pictures')
      .list(restaurantId);
    
    if (foldersError) {
      console.error(`Error listing folders for restaurant ${restaurantId}:`, foldersError);
      return result;
    }
    
    // Process each category folder
    for (const category of ['gallery', 'menu', 'interior', 'exterior'] as const) {
      if (folders?.some(f => f.name === category)) {
        // List files in the category folder
        const { data: files, error: filesError } = await supabase.storage
          .from('venue-pictures')
          .list(`${restaurantId}/${category}`);
        
        if (filesError) {
          console.error(`Error listing ${category} files for restaurant ${restaurantId}:`, filesError);
          continue;
        }
        
        // Get public URL for each file
        for (const file of files || []) {
          const { data } = supabase.storage
            .from('venue-pictures')
            .getPublicUrl(`${restaurantId}/${category}/${file.name}`);
          
          result[category].push(data.publicUrl);
        }
      }
    }
    
    return result;
  } catch (err) {
    console.error(`Error getting storage URLs for restaurant ${restaurantId}:`, err);
    return result;
  }
};

// Function to validate image file before upload
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return { valid: false, error: `File size exceeds 5MB limit (${(file.size / (1024 * 1024)).toFixed(2)}MB)` };
  }
  
  // Check file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return { valid: false, error: `Invalid file type. Allowed types: JPEG, PNG, GIF, WEBP` };
  }
  
  return { valid: true };
};

// Function to check if a URL is valid and accessible
export const isValidImageUrl = async (url: string): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const response = await fetch(url, { 
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-cache' // Prevent caching of the HEAD request
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn(`URL validation failed with status ${response.status}: ${url}`);
      return false;
    }

    const contentType = response.headers.get('content-type');
    if (!contentType?.startsWith('image/')) {
      console.warn(`Invalid content type for URL ${url}: ${contentType}`);
      return false;
    }

    // Additional check: try to get the actual image data
    const imageResponse = await fetch(url, { 
      method: 'GET',
      signal: controller.signal,
      cache: 'no-cache'
    });

    if (!imageResponse.ok) {
      console.warn(`Image data fetch failed with status ${imageResponse.status}: ${url}`);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error validating image URL:', err);
    return false;
  }
};

export type PictureCategory = 'gallery' | 'menu' | 'interior' | 'exterior';